// Task 상태 관련 유틸리티 함수들

export type TaskStatus = '완료' | '진행중' | '대기' | '취소';
export type AlertLevel = 'warning' | 'error' | null;

/**
 * Task의 최종 상태를 결정하는 함수
 * 마감일, 진행상태, 경고레벨을 모두 고려하여 최종 상태를 반환
 */
export const getTaskFinalStatus = (
  progress: TaskStatus,
  alertLevel?: AlertLevel,
  deadline?: string
): 'completed' | 'critical' | 'urgent' | 'warning' | 'error' | 'inProgress' | 'pending' | 'cancelled' => {
  // 완료된 작업은 항상 완료 상태 (마감일 조건 무시)
  if (progress === '완료') return 'completed';
  
  // 마감일 조건 확인
  if (deadline) {
    try {
      const now = new Date();
      const deadlineDate = new Date(deadline);
      const diffTime = deadlineDate.getTime() - now.getTime();
      const diffHours = diffTime / (1000 * 60 * 60);
      
      // 마감일이 지났으면 critical (가장 높은 우선순위)
      if (diffTime < 0) return 'critical';
      
      // 6시간 이내이면 urgent (alertLevel보다 우선)
      if (diffHours <= 6) return 'urgent';
    } catch {
      // 날짜 파싱 에러시 무시
    }
  }
  
  // 경고/에러 레벨
  if (alertLevel === 'error') return 'error';
  if (alertLevel === 'warning') return 'warning';
  
  // 기본 상태별 분류
  switch (progress) {
    case '진행중': return 'inProgress';
    case '대기': return 'pending';
    case '취소': return 'cancelled';
    default: return 'pending';
  }
};

/**
 * Task 상태에 따른 색상 반환 (미니맵용 - 헥스 코드)
 */
export const getTaskStatusColorHex = (
  progress: TaskStatus,
  alertLevel?: AlertLevel,
  deadline?: string
): string => {
  const status = getTaskFinalStatus(progress, alertLevel, deadline);
  
  switch (status) {
    case 'completed': return '#10b981';  // emerald-500 (초록색)
    case 'critical': return '#dc2626';   // red-600 (빨간색)
    case 'urgent': return '#f97316';     // orange-500 (주황색)
    case 'error': return '#ef4444';      // red-500 (빨간색)
    case 'warning': return '#f97316';    // orange-500 (주황색)
    case 'inProgress': return '#3b82f6'; // blue-500 (파란색)
    case 'pending': return '#d1d5db';    // gray-300 (회색)
    case 'cancelled': return '#374151';  // gray-700 (어두운 회색)
    default: return '#d1d5db';           // gray-300 (회색)
  }
};

/**
 * Task 상태에 따른 텍스트 색상 클래스 반환 (마감일 텍스트용)
 * 마감일 텍스트는 위험도만 반영하고, 기본은 검정색
 */
export const getTaskStatusTextClass = (
  progress: TaskStatus,
  alertLevel?: AlertLevel,
  deadline?: string
): string => {
  const status = getTaskFinalStatus(progress, alertLevel, deadline);
  
  switch (status) {
    case 'critical': return 'text-red-600 font-medium';    // 마감일 지남
    case 'urgent': return 'text-orange-500 font-medium';   // 6시간 이내
    case 'error': return 'text-red-600 font-medium';       // 오류
    case 'warning': return 'text-orange-500 font-medium';  // 경고
    default: return 'text-gray-900';                       // 기본 (검정색)
  }
};

/**
 * Task 상태에 따른 배경 색상 클래스 반환 (카드 배경용)
 */
export const getTaskStatusBackgroundClass = (
  progress: TaskStatus,
  alertLevel?: AlertLevel,
  deadline?: string
): string => {
  const status = getTaskFinalStatus(progress, alertLevel, deadline);
  
  switch (status) {
    case 'completed': return 'bg-emerald-50 border-emerald-200';
    case 'critical': return 'bg-red-50 border-red-200';
    case 'urgent': return 'bg-orange-50 border-orange-200';
    case 'error': return 'bg-red-50 border-red-200';
    case 'warning': return 'bg-orange-50 border-orange-200';
    case 'inProgress': return 'bg-blue-50 border-blue-200';
    case 'pending': return 'bg-white border-gray-200';
    case 'cancelled': return 'bg-slate-100 border-slate-300';
    default: return 'bg-white border-gray-200';
  }
};

/**
 * Task 상태에 따른 스프레드시트용 배지 색상 클래스 반환
 */
export const getTaskStatusBadgeClass = (
  progress: TaskStatus,
  alertLevel?: AlertLevel,
  deadline?: string
): string => {
  const status = getTaskFinalStatus(progress, alertLevel, deadline);
  
  switch (status) {
    case 'completed': return 'bg-green-100 text-green-800';
    case 'critical': return 'bg-red-100 text-red-800';
    case 'urgent': return 'bg-orange-100 text-orange-800';
    case 'error': return 'bg-red-100 text-red-800';
    case 'warning': return 'bg-orange-100 text-orange-800';
    case 'inProgress': return 'bg-blue-100 text-blue-800';
    case 'pending': return 'bg-gray-100 text-gray-800';
    case 'cancelled': return 'bg-slate-100 text-slate-800';
    default: return 'bg-gray-100 text-gray-800';
  }
};
